// Verwaltung der Ticket-Checklisten-Vorlagen — läuft im Formularbereich der Pille
// (Bausteine der Pillen-Formulare; die Pille liefert Kopfzeile + Schließen).
// Vorlagen enthalten nur den Ablauf (eine Zeile pro Punkt) — ☐ ergänzt der Save automatisch.
import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, CheckSquare, FloppyDisk, Info, ListBullets, ListChecks, Plus, ToggleRight } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { normalisiereCheckliste, zaehleChecklistenFortschritt, type ChecklistenVorlage } from '../../../lib/tickets/checklistenVorlagen';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld } from '../../../components/island/form/FormFeld';
import { FormSchalter } from '../../../components/island/form/FormSchalter';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { FormLoeschKnopf } from '../../../components/island/form/FormLoeschKnopf';
import { FormListenZeile } from '../../../components/island/form/FormListenZeile';
import { FormHinweis } from '../../../components/island/form/FormHinweis';
import { FormLaden } from '../../../components/island/form/FormLaden';
import { FormText } from '../../../components/island/form/FormText';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { ABSCHNITT_FARBE } from '../../../components/island/form/form-stil';

interface Entwurf {
  id: string | null;
  name: string;
  checkliste: string;
  aktiv: boolean;
}

export default function ChecklistenVorlagenVerwaltung() {
  const [vorlagen, setVorlagen] = useState<ChecklistenVorlage[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const fehler = useFormFehler();

  const lade = async () => {
    const { data, error } = await supabase
      .from('ticket_checklisten_vorlagen')
      .select('id, name, checkliste, sortierung, aktiv')
      .order('sortierung', { ascending: true })
      .order('name', { ascending: true });
    if (!error) setVorlagen((data as ChecklistenVorlage[]) || []);
    setLaedt(false);
  };
  useEffect(() => { void lade(); }, []);

  const handleSpeichern = async () => {
    if (!entwurf || speichert) return;
    const name = entwurf.name.trim();
    if (!name) { fehler.zeige('Bitte einen Namen angeben.', ['name']); return; }
    setSpeichert(true);
    fehler.leeren();
    const zeile = { name, checkliste: normalisiereCheckliste(entwurf.checkliste), aktiv: entwurf.aktiv };
    const res = entwurf.id
      ? await supabase.from('ticket_checklisten_vorlagen').update(zeile).eq('id', entwurf.id)
      : await supabase.from('ticket_checklisten_vorlagen').insert(zeile);
    setSpeichert(false);
    if (res.error) { fehler.zeige(res.error.message); return; }
    setEntwurf(null);
    await lade();
  };

  // Rückfrage (früher window.confirm) steckt im Zwei-Klick-Knopf, Text unverändert.
  const handleLoeschen = async () => {
    if (!entwurf?.id || speichert) return;
    setSpeichert(true);
    const { error } = await supabase.from('ticket_checklisten_vorlagen').delete().eq('id', entwurf.id);
    setSpeichert(false);
    if (error) { fehler.zeige(error.message); return; }
    setEntwurf(null);
    await lade();
  };

  if (laedt) return <FormLaden />;

  // ── Editor ────────────────────────────────────────────────────────────────
  if (entwurf) {
    return (
      <FormRahmen fehler={fehler.text} knopfleiste={
        <FormKnopfleiste
          abbrechen={{ label: 'Zur Liste', icon: ArrowLeft, onClick: () => { setEntwurf(null); fehler.leeren(); }, tip: 'Zur Liste – Ohne Speichern zurück zur Liste' }}
          haupt={{ label: 'Speichern', icon: FloppyDisk, typ: 'button', onClick: handleSpeichern, laedt: speichert, tip: 'Speichern – Vorlage speichern' }}
          zusatz={entwurf.id ? (
            <FormLoeschKnopf frage={`Vorlage „${entwurf.name}" wirklich löschen?`} tip="Vorlage löschen" gesperrt={speichert}
              onLoeschen={() => void handleLoeschen()} />
          ) : undefined} />
      }>
        <FormAbschnitt titel={entwurf.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'} icon={CheckSquare} farbe="gruen" pflicht={1}
          fehlt={fehler.istFehler('name') ? 1 : 0} index={0}>
          <FormFeld label="Name" value={entwurf.name} placeholder="z. B. Neuvermietung" pflicht fehler={fehler.istFehler('name')}
            onChange={(e) => { setEntwurf({ ...entwurf, name: e.target.value }); fehler.korrigiert('name'); }} />
        </FormAbschnitt>
        <FormAbschnitt titel="Checkliste" icon={ListBullets} farbe="violett" index={1}>
          <FormFeld label="Checkliste — eine Zeile pro Punkt (☐ wird automatisch ergänzt)" value={entwurf.checkliste} zeilen={12}
            onChange={(e) => setEntwurf({ ...entwurf, checkliste: e.target.value })}
            placeholder={'1. Vorbereitung\nObjektinformationen prüfen\nMietpreis prüfen\n\n2. Vermarktung\nFotos prüfen oder erstellen\n…'}
            hinweis={<>Nummerierte Zeilen („1. Abschnitt") werden fette Überschriften ohne Kästchen — für Überschriften ohne Nummer ein „#" voranstellen („# Erforderliche Unterlagen").</>} />
        </FormAbschnitt>
        <FormAbschnitt titel="Status" icon={ToggleRight} farbe="orange" index={2}>
          <FormSchalter text="Aktiv (im Ticket-Formular auswählbar)" an={entwurf.aktiv} icon={CheckCircle} verlauf={ABSCHNITT_FARBE.gruen.verlauf}
            onChange={(an) => setEntwurf({ ...entwurf, aktiv: an })} />
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // ── Liste ─────────────────────────────────────────────────────────────────
  return (
    <FormRahmen fehler={fehler.text} knopfleiste={
      <FormKnopfleiste haupt={{
        label: 'Neue Vorlage', icon: Plus, typ: 'button', tip: 'Neue Vorlage – Neue Checklisten-Vorlage anlegen',
        onClick: () => { setEntwurf({ id: null, name: '', checkliste: '', aktiv: true }); fehler.leeren(); },
      }} />
    }>
      <FormAbschnitt titel="Vorlagen" icon={ListChecks} farbe="gruen" index={0}>
        <FormHinweis icon={Info} farbe="gold">
          Vorlagen enthalten nur den Ablauf — beim Erstellen eines Tickets wird die Checkliste in die Beschreibung kopiert und dort abgearbeitet.
        </FormHinweis>
        {vorlagen.length === 0 && <FormText leer>Noch keine Vorlagen.</FormText>}
        {vorlagen.map((v) => {
          const punkte = zaehleChecklistenFortschritt(v.checkliste).gesamt;
          return (
            <FormListenZeile key={v.id} titel={v.name} oben={`${punkte} Punkte${v.aktiv ? '' : ' · inaktiv'}`} icon={CheckSquare}
              tip="Vorlage bearbeiten" onClick={() => setEntwurf({ id: v.id, name: v.name, checkliste: v.checkliste, aktiv: v.aktiv })} />
          );
        })}
      </FormAbschnitt>
    </FormRahmen>
  );
}
